import React, { useState, useEffect, useRef } from 'react';
import { BhiDialog } from 'src/UIKit/components/Dialog';
import {
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  TextField,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QRCodeStyling from 'qr-code-styling';
import { useCompanyPhoneApi, CompanyPhone } from 'src/modules/companies/api/CompanyPhoneApi';
import { useAppSelector } from 'src/store';
import { getCurrentCompanyId } from 'src/modules/companies/slices/companiesSlice';
import {
  DEFAULT_QR_SAVE_PARAMETERS,
  getDestinationDataFromQrData,
  getQRCodeSaveParameters,
  handleDownload as handleDownloadImpl,
  renderQrCodePreview,
} from 'src/modules/qrCodes/helpers/helpers';
import { useGetKeywordQRCodeApi } from 'src/modules/qrCodes/api';
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import {
  QR_CODE_SIZES,
  QRCodeDestinationType,
  QRCodeFileFormat,
  QRCodeSaveParameters,
  QRCodeSize,
  QRCodeStylingOptions,
} from 'src/modules/qrCodes/types';
import { getCurrentCompanyName } from 'src/modules/companies/slices';
import { useSelector } from 'react-redux';

interface CreateQRCodeModalProps {
  open: boolean;
  onClose: () => void;
  keyword: {
    id: string;
    name: string;
  } | null;
}

export const CreateQRCodeModal: React.FC<CreateQRCodeModalProps> = ({ open, onClose, keyword }) => {
  const [phones, setPhones] = useState<CompanyPhone[]>([]);
  const [qrCodeData, setQrCodeData] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const qrCodeInstance = useRef<QRCodeStyling | null>(null);

  const currentCompanyId = useAppSelector(getCurrentCompanyId);
  const currentCompanyName = useSelector(getCurrentCompanyName);
  const companyPhoneApi = useCompanyPhoneApi();
  const getKeywordQRCodeApi = useGetKeywordQRCodeApi();

  const [stylingOptions, setStylingOptions] = useState<QRCodeStylingOptions | null>(null);
  const [downloadSize, setDownloadSize] = useState<QRCodeSize | null>(null);
  const [downloadFormat, setDownloadFormat] = useState<QRCodeFileFormat>(
    DEFAULT_QR_SAVE_PARAMETERS.previewFormat,
  );
  const [isDataReady, setIsDataReady] = useState(false);

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        await Promise.all([loadPhones(), loadQRCodeData()]);
        setIsDataReady(true);
      } catch (error) {
        console.error('Failed to load initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open && keyword) {
      // Clear any existing QR code
      if (qrCodeRef.current) {
        qrCodeRef.current.innerHTML = '';
      }
      qrCodeInstance.current = null;

      // Reset states
      setIsDataReady(false);
      setLoading(true);

      // Load company phones and presets when component opens
      getData();
    }
  }, [open, currentCompanyId, keyword]);

  // Combined effect: Update QR code data and generate QR code
  useEffect(() => {
    if (!open) return;

    const renderQRCode = async () => {
      // First, update the data
      if (keyword && qrCodeData) {
        setIsDataReady(true);

        if (qrCodeRef.current) {
          // Clear the container first
          qrCodeRef.current.innerHTML = '';

          // Create QR options with the new data
          // Calculate display size to fit within container (337px height, ~300px width with padding)
          const containerHeight = 285;
          const containerWidth = 300; // Approximate width with padding
          const paddingTotal = 32; // 16px padding on each side (2 * 16px)
          const maxDisplaySize = Math.min(
            containerHeight - paddingTotal,
            containerWidth - paddingTotal,
          );

          const sizeConfig =
            QR_CODE_SIZES[downloadSize || DEFAULT_QR_SAVE_PARAMETERS.previewSize];
          const previewBoxSize = Math.min(sizeConfig.width, maxDisplaySize);
          const options: QRCodeStylingOptions =
            stylingOptions || DEFAULT_QR_SAVE_PARAMETERS.styling;
          const destinationType: QRCodeDestinationType = 'SMS';
          const instance = await renderQrCodePreview({
            container: qrCodeRef.current,
            data: qrCodeData,
            size: previewBoxSize,
            styling: options,
            destinationType,
            margin: 5,
          });

          qrCodeInstance.current = instance;
        }
      } else {
        setIsDataReady(false);
      }
    };

    renderQRCode();
  }, [open, keyword, qrCodeData, stylingOptions]);

  // Cleanup QR code instance when modal closes
  useEffect(() => {
    if (!open && qrCodeInstance.current) {
      qrCodeInstance.current = null;
    }
  }, [open]);

  const loadPhones = async () => {
    if (!currentCompanyId) return;

    try {
      const response = await companyPhoneApi.getCompanyPhones(currentCompanyId);
      setPhones(response.data);
    } catch (error) {
      console.error('Failed to load company phones:', error);
    }
  };

  // Only a code stored for this keyword carries styling of its own; the generated one is the
  // keyword itself and leaves the defaults in place.
  const loadQRCodeData = async () => {
    if (!currentCompanyId || !keyword) return;
    try {
      const { data: qrCode } = await getKeywordQRCodeApi(keyword.id, currentCompanyId);
      setQrCodeData(qrCode.qrCodeData);

      if (!qrCode.generated) {
        const parameters: QRCodeSaveParameters = getQRCodeSaveParameters(qrCode.parameters);
        setStylingOptions(parameters.styling);
        setDownloadSize(parameters.previewSize);
        setDownloadFormat(parameters.previewFormat);
      }
    } catch (error) {
      // The destination comes from here alone, so a silent failure leaves an empty frame and a
      // dead button with nothing said.
      reportErrorToUser(error, 'Could not load the QR code.');
    }
  };

  const handleDownload = async () => {
    if (!qrCodeInstance.current || !keyword || !qrCodeData) return;

    const destinationData = getDestinationDataFromQrData(qrCodeData, 'SMS');
    const formData = {
      styling: stylingOptions || DEFAULT_QR_SAVE_PARAMETERS.styling,
      previewFormat: downloadFormat || DEFAULT_QR_SAVE_PARAMETERS.previewFormat,
      previewSize: downloadSize || DEFAULT_QR_SAVE_PARAMETERS.previewSize,
      destinationData,
    };

    await handleDownloadImpl(formData, 'SMS', `Keyword: ${keyword.name}`, currentCompanyName, qrCodeData);
  };

  const handleClose = () => {
    setIsDataReady(false);
    setQrCodeData('');
    setPhones([]);
    setStylingOptions(null);
    setDownloadSize(null);
    setDownloadFormat(DEFAULT_QR_SAVE_PARAMETERS.previewFormat);
    // Clear QR code container
    if (qrCodeRef.current) {
      qrCodeRef.current.innerHTML = '';
    }
    qrCodeInstance.current = null;
    onClose();
  };

  // Check if company has at least one phone number
  const hasPhoneNumbers = phones.length > 0;

  // The number the scan dials is the one the code carries, not one chosen on this screen: a stored
  // code keeps the number it was saved against, and the QR codes list answers with that same one.
  const sendToNumber = qrCodeData.split(':')[1] ?? '';
  const sendToLabel = phones.find(p => p.number == sendToNumber)?.label;

  return (
    <BhiDialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      title={`QR Code for ${keyword?.name}`}
      showCloseButton={false}
      PaperProps={{
        sx: {
          width: 400,
          maxHeight: '90vh',
        },
      }}
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 2, pb: 1 }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading QR code settings...
            </Typography>
          </Box>
        ) : !hasPhoneNumbers ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Phone Numbers Available
            </Typography>
            <Typography color="text.secondary">
              Please add at least one phone number to your company to generate QR codes.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Single Column Layout */}
            <Box sx={{ width: '100%' }}>
              {/* Send To Field */}
              <TextField
                sx={{ mb: 2, '& .MuiInputBase-input': { cursor: 'not-allowed' } }}
                label="Send To"
                value={`${sendToNumber}${sendToLabel ? ` - ${sendToLabel}` : ''}`}
                variant="outlined"
                InputProps={{ readOnly: true, disabled: true }}
                fullWidth
              />

              {/* QR Code Data Display */}
              <TextField
                sx={{ mb: 2, '& .MuiInputBase-input': { cursor: 'not-allowed' } }}
                autoComplete="off"
                InputLabelProps={{ shrink: true }}
                label="Keyword"
                value={keyword?.name}
                variant="outlined"
                InputProps={{ readOnly: true, disabled: true }}
                fullWidth
              />

              {/* QR Code Preview */}
              <Box
                ref={qrCodeRef}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'grey.100',
                  height: 250,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  padding: 2,
                  mb: 2,
                  '& > *': {
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  },
                }}
              />

              {/* Download Format Dropdown */}
              <FormControl sx={{ mb: 2, width: '100%' }}>
                <InputLabel>Download Format</InputLabel>
                <Select
                  size="small"
                  label="Download Format"
                  MenuProps={{ disableScrollLock: true }}
                  value={downloadFormat}
                  onChange={e => setDownloadFormat(e.target.value as QRCodeFileFormat)}
                >
                  <MenuItem value="PNG">PNG</MenuItem>
                  <MenuItem value="JPG">JPG</MenuItem>
                  <MenuItem value="SVG">SVG</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleDownload}
          variant="contained"
          fullWidth
          size="large"
          disabled={!hasPhoneNumbers || !isDataReady}
        >
          Download
        </Button>
      </DialogActions>
    </BhiDialog>
  );
};
